/**
 * Browses and transfers files on the device, over the `files` data channel.
 *
 * Bytes stream over the data channel rather than through adb's own sync protocol, which is
 * tuned for USB and degrades badly over a high-latency link. The device does the last hop
 * with adb (see FileTransferBridge.kt).
 *
 * Wire format, matching the phone side:
 * - Up, text: `{"t":"list"|"get"|"delete","path":…}`, `{"t":"put","path":…,"size":N}`, `{"t":"cancel"}`
 * - Up, binary: chunks of the file being uploaded, in order
 * - Down, text: `{"t":"listing",…}`, `{"t":"meta","size":N}`, `{"t":"done"}`,
 *   `{"t":"deleted","path":…}`, `{"t":"error","message":…}`
 * - Down, binary: chunks of the file being downloaded, in order
 *
 * One transfer at a time, which keeps chunk ordering unambiguous on a single channel.
 */

/** Payload per message. Large sends reset a data channel, so stay well under the limit. */
const CHUNK = 16 * 1024;

/** Stop feeding the channel above this and wait for it to drain. */
const HIGH_WATER = 4 * 1024 * 1024;
const LOW_WATER = 1024 * 1024;

export interface Entry {
  /** Name within the current directory, not a full path. */
  n: string;
  /** True for a directory. */
  d: boolean;
  /** Size in bytes. */
  s: number;
  /** Modified time, seconds since the epoch. */
  m: number;
}

export interface Listing {
  path: string;
  entries: Entry[];
  truncated: boolean;
}

interface Download {
  path: string;
  size: number;
  received: number;
  chunks: Uint8Array[];
}

interface Callbacks {
  onListing: (listing: Listing) => void;
  onStatus: (text: string, busy: boolean) => void;
}

export class FileTransfer {
  private channel: RTCDataChannel | null = null;
  private download: Download | null = null;
  private busy = false;

  /** Where the browser is pointed. Uploads and refreshes use it. */
  cwd = '/sdcard';

  constructor(private readonly callbacks: Callbacks) {}

  attach(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.binaryType = 'arraybuffer';
    channel.bufferedAmountLowThreshold = LOW_WATER;
    channel.onmessage = (event) => {
      if (typeof event.data === 'string') {
        this.handleControl(event.data);
      } else if (this.download) {
        const chunk = new Uint8Array(event.data as ArrayBuffer);
        this.download.chunks.push(chunk);
        this.download.received += chunk.length;
        this.report();
      }
    };
  }

  get isReady(): boolean {
    return this.channel?.readyState === 'open';
  }

  get isBusy(): boolean {
    return this.busy;
  }

  /** Lists a directory and moves the browser there. */
  list(path = this.cwd): void {
    if (!this.send({ t: 'list', path })) return;
    this.callbacks.onStatus(`Loading ${path}…`, true);
  }

  /** Goes to the parent of the current directory. */
  parent(): void {
    if (this.cwd === '/') return;
    const up = this.cwd.replace(/\/[^/]+\/?$/, '') || '/';
    this.list(up);
  }

  /** Joins a name onto the current directory. */
  resolve(name: string): string {
    return this.cwd === '/' ? `/${name}` : `${this.cwd}/${name}`;
  }

  /** Fetches a device file and hands it to the browser as a download. */
  requestDownload(path: string): void {
    if (this.busy) return;
    if (!this.send({ t: 'get', path })) return;
    this.busy = true;
    this.download = { path, size: 0, received: 0, chunks: [] };
    this.callbacks.onStatus(`Requesting ${path.split('/').pop()}…`, true);
  }

  /** Removes a file, or an empty directory. The phone refuses a non-empty one. */
  requestDelete(path: string): void {
    if (this.busy) return;
    if (!this.send({ t: 'delete', path })) return;
    this.busy = true;
    this.callbacks.onStatus(`Deleting ${path.split('/').pop()}…`, true);
  }

  /** Sends local files into the current directory, one after another. */
  async upload(files: File[]): Promise<void> {
    for (const file of files) {
      if (!this.isReady) return;
      await this.uploadOne(file, this.resolve(file.name));
    }
  }

  dispose(): void {
    if (this.channel) {
      this.channel.onmessage = null;
      this.channel = null;
    }
    this.download = null;
    this.busy = false;
  }

  private async uploadOne(file: File, destination: string): Promise<void> {
    const channel = this.channel;
    if (!channel || channel.readyState !== 'open') return;
    this.busy = true;

    try {
      channel.send(JSON.stringify({ t: 'put', path: destination, size: file.size }));
      const reader = file.stream().getReader();
      let sent = 0;
      let carry = new Uint8Array(0);

      // A stream chunk can be any size, so re-slice to CHUNK before sending.
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        carry = concat(carry, value);
        while (carry.length >= CHUNK) {
          await this.sendChunk(channel, carry.subarray(0, CHUNK));
          carry = carry.slice(CHUNK);
          sent += CHUNK;
          this.callbacks.onStatus(`${file.name}: ${percent(sent, file.size)}`, true);
        }
      }
      if (carry.length > 0) {
        await this.sendChunk(channel, carry);
        sent += carry.length;
      }
      this.callbacks.onStatus(`${file.name}: writing on the device…`, true);
      // The phone answers `done` once the push lands, which resolves this upload.
      await this.awaitCompletion();
    } catch (error) {
      this.busy = false;
      this.callbacks.onStatus(`Upload failed: ${(error as Error).message}`, false);
    }
  }

  /** Resolves when the phone reports the current operation finished or failed. */
  private awaitCompletion(): Promise<void> {
    return new Promise((resolve) => {
      this.completion = resolve;
    });
  }

  private completion: (() => void) | null = null;

  private settle(): void {
    const done = this.completion;
    this.completion = null;
    done?.();
  }

  /** Writes one chunk, waiting when the channel is backed up. */
  private async sendChunk(channel: RTCDataChannel, chunk: Uint8Array): Promise<void> {
    if (channel.bufferedAmount > HIGH_WATER) {
      await new Promise<void>((resolve) => {
        const onLow = (): void => {
          channel.removeEventListener('bufferedamountlow', onLow);
          resolve();
        };
        channel.addEventListener('bufferedamountlow', onLow);
      });
    }
    // A copy, because send() on a view of a reused stream buffer is not safe.
    channel.send(chunk.slice().buffer);
  }

  private send(message: Record<string, unknown>): boolean {
    if (!this.isReady) {
      this.callbacks.onStatus('No file channel on this session.', false);
      return false;
    }
    this.channel!.send(JSON.stringify(message));
    return true;
  }

  private handleControl(text: string): void {
    let message: {
      t: string;
      size?: number;
      message?: string;
      path?: string;
      entries?: Entry[];
      truncated?: boolean;
    };
    try {
      message = JSON.parse(text) as typeof message;
    } catch {
      return;
    }

    switch (message.t) {
      case 'listing':
        this.cwd = message.path ?? this.cwd;
        this.callbacks.onListing({
          path: this.cwd,
          entries: message.entries ?? [],
          truncated: message.truncated ?? false,
        });
        this.callbacks.onStatus(
          message.truncated ? 'Listing truncated: too many entries.' : '',
          false,
        );
        break;

      case 'meta':
        if (this.download) {
          this.download.size = message.size ?? 0;
          this.report();
        }
        break;

      case 'deleted':
        this.busy = false;
        this.callbacks.onStatus(`Deleted ${message.path?.split('/').pop() ?? ''}.`, false);
        this.list(); // the listing is now stale
        break;

      case 'done':
        if (this.download) {
          this.finishDownload();
        } else {
          this.busy = false;
          this.callbacks.onStatus('Upload complete.', false);
          this.settle();
          this.list();
        }
        break;

      case 'error':
        this.download = null;
        this.busy = false;
        this.callbacks.onStatus(message.message ?? 'Transfer failed.', false);
        this.settle();
        break;
    }
  }

  /** Assembles the received chunks and triggers a browser save. */
  private finishDownload(): void {
    const active = this.download;
    this.download = null;
    this.busy = false;
    if (!active) return;

    const blob = new Blob(active.chunks as BlobPart[]);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = active.path.split('/').pop() || 'download';
    link.click();
    URL.revokeObjectURL(url);
    this.callbacks.onStatus(`Saved ${link.download} (${formatBytes(blob.size)}).`, false);
  }

  private report(): void {
    const active = this.download;
    if (!active) return;
    const name = active.path.split('/').pop() ?? '';
    this.callbacks.onStatus(
      active.size ? `${name}: ${percent(active.received, active.size)}` : `${name}: ${formatBytes(active.received)}`,
      true,
    );
  }
}

/** Always copies into a fresh buffer, so the caller never holds a view of stream memory. */
function concat(a: Uint8Array, b: Uint8Array) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

function percent(done: number, total: number): string {
  if (!total) return formatBytes(done);
  return `${Math.min(100, Math.round((done / total) * 100))}% of ${formatBytes(total)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** A coarse type icon per entry. Thumbnails would mean pulling file bytes to browse. */
export function iconFor(entry: Entry): string {
  if (entry.d) return '📁';
  const ext = entry.n.includes('.') ? entry.n.split('.').pop()!.toLowerCase() : '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif'].includes(ext)) return '🖼';
  if (['mp4', 'mkv', 'webm', 'mov', '3gp', 'avi'].includes(ext)) return '🎬';
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'].includes(ext)) return '🎵';
  if (['zip', 'tar', 'gz', 'xz', 'rar', '7z', 'apk', 'jar'].includes(ext)) return '📦';
  if (['pdf'].includes(ext)) return '📕';
  if (['txt', 'log', 'json', 'xml', 'md', 'csv', 'ini', 'conf'].includes(ext)) return '📄';
  return '📄';
}
